import { useNavigate } from "react-router-dom";
import { useCallback, useRef, useState } from "react";
import PopSnackbar from "../../components/snackbar";
import { keyframes, styled } from "@mui/material/styles";
import { Tabs, Tab, Box } from "@mui/material";
import CampaignIcon from "@mui/icons-material/Campaign";
import LoopIcon from "@mui/icons-material/Loop";
import { Check } from "react-feather";
import { v4 as uuidv4 } from "uuid";
import moment from "moment";
import {
  CreateCampaign,
  CreateCampaignDraft,
  DeleteCampaign,
  GenerateCampaignPlaylist,
} from "../../services/campaign";
import {
  AddVideoToCampaign,
  DeleteVideoGlobal,
  UploadVideo,
} from "../../services/video";
import AlertModal from "../../components/modal";
import AddLoopStep from "./tabs/add-loop";
import AddCampaignStep from "./tabs/add-campaign";

const slideIn = keyframes`
  from {
    opacity: 0;
    transform: translateY(20px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
`;

const StyledTabs = styled(Tabs)(({ theme }) => ({
  position: "relative",
  width: "100%",
  background: "linear-gradient(135deg, #fafafa, #fff)",
  borderRadius: "8px 8px 0 0",
  boxShadow: "0 1px 1px rgba(0,0,0,0.1)",
  overflow: "hidden",
  "& .MuiTabs-indicator": {
    display: "none",
  },
}));

const StyledTab = styled((props) => <Tab disableRipple {...props} />)(
  ({ theme }) => ({
    textTransform: "none",
    fontSize: "1rem",
    padding: theme.spacing(2, 1),
    fontFamily: "SeagoeUI, sans-serif",
    color: "rgba(0, 0, 0, 0.65)",
    opacity: 0.85,
    transition: "all 0.3s ease",
    position: "relative",
    width: "50%",
    borderRight: "1px solid rgba(0, 0, 0, 0.05)",
    "&:last-of-type": {
      borderRight: "none",
    },
    "& .MuiTab-iconWrapper": {
      marginRight: theme.spacing(1),
      fontSize: 22,
      transition: "transform 0.3s ease",
    },
    "&:hover": {
      backgroundColor: "rgba(0, 0, 0, 0.03)",
      color: "#000",
      opacity: 1,
      "& .MuiTab-iconWrapper": {
        transform: "scale(1.1)",
      },
    },
    "&.Mui-selected": {
      color: "#000",
      opacity: 1,
      fontFamily: "SeagoeUIBold, sans-serif",
    },
  })
);

const StepIndicator = styled("span")(({ active, completed }) => ({
  width: "30px",
  height: "30px",
  borderRadius: "50%",
  display: "inline-flex",
  alignItems: "center",
  justifyContent: "center",
  marginRight: "12px",
  fontSize: "0.875rem",
  backgroundColor: completed ? "#000" : active ? "rgba(0, 0, 0, 0.1)" : "#fff",
  color: completed ? "#fff" : active ? "#000" : "rgba(0, 0, 0, 0.75)",
  border: `2px solid ${
    completed
      ? "#000"
      : active
      ? "rgba(97, 97, 97, 0.4)"
      : "rgba(97, 97, 97, 0.2)"
  }`,
  transition: "all 0.3s ease",
  transform: active ? "scale(1.1)" : "scale(1)",
}));

const TabPanel = styled(Box)(({ theme }) => ({
  padding: theme.spacing(4, 2),
  animation: `${slideIn} 0.5s cubic-bezier(0.4, 0, 0.2, 1)`,
  "& > *": {
    animation: `${slideIn} 0.5s cubic-bezier(0.4, 0, 0.2, 1)`,
  },
}));

const ProgressArrow = styled("div")(({ activeTab }) => ({
  position: "absolute",
  left: activeTab === 0 ? "25%" : "75%",
  transform: "translateX(-50%)",
  width: 0,
  height: 0,
  borderLeft: "10px solid transparent",
  borderRight: "10px solid transparent",
  borderTop: "10px solid #ff4757",
  transition: "left 0.4s cubic-bezier(0.4, 0, 0.2, 1)",
  "&::before, &::after": {
    content: '""',
    position: "absolute",
    width: "4px",
    height: "4px",
    borderRadius: "50%",
    background: "#ff4757",
    animation: "particles 1.5s infinite",
    opacity: 0,
  },
  "&::before": {
    animationDelay: "0.2s",
    left: "-15px",
  },
  "&::after": {
    animationDelay: "0.4s",
    right: "-15px",
  },
  "@keyframes particles": {
    "0%": {
      transform: "translateY(0) scale(1)",
      opacity: 0,
    },
    "50%": {
      transform: "translateY(-20px) scale(0.5)",
      opacity: 0.8,
    },
    "100%": {
      transform: "translateY(-40px) scale(0)",
      opacity: 0,
    },
  },
}));

export default function AddCampaign() {
  const navigate = useNavigate();
  const campaignFormikRef = useRef(null);
  const loopStepRef = useRef(null);

  const [activeTab, setActiveTab] = useState(0);

  const [campaignData, setCampaignData] = useState({
    name: "",
    client: "",
    observations: "",
    schedules: [],
  });

  const [loopsData, setLoopsData] = useState({
    selectedDevices: [],
    filters: {
      networks: [],
      partners: [],
      counties: [],
      cities: [],
      locations: [],
      tags: [],
      search: "",
    },
    timeInterval: 60,
  });
  const [openModal, setOpenModal] = useState(false);
  const [alertModalText, setAlertModalText] = useState("");
  const [toastStatus, setToastStatus] = useState(false);
  const [toastDescription, setToastDescription] = useState("");
  const [loading, setLoading] = useState(false);
  const [createdCampaignID, setCreatedCampaignID] = useState(null);

  const handleTabChange = async (_, newTab) => {
    if (loading) return;
    try {
      if (activeTab === 0 && newTab === 1) {
        // First validate form data before submitting
        const { name, client } = campaignFormikRef.current?.values || {};
        if (!name?.trim() || !client?.trim()) {
          handleStatus("Numele campaniei si al clientului sunt obligatorii.");
          return;
        }
        if (!campaignData.schedules || campaignData.schedules.length === 0) {
          handleStatus(
            "Adauga cel putin o perioada cu video-uri inainte de a continua."
          );
          return;
        }

        const schedules = campaignData.schedules;

        // Check for duplicate periods (same start and end dates)
        const duplicatePeriods = [];
        for (let i = 0; i < schedules.length; i++) {
          for (let j = i + 1; j < schedules.length; j++) {
            if (
              schedules[i].startDate === schedules[j].startDate &&
              schedules[i].endDate === schedules[j].endDate
            ) {
              duplicatePeriods.push({
                period1: i + 1,
                period2: j + 1,
                startDate: schedules[i].startDate,
                endDate: schedules[i].endDate,
              });
            }
          }
        }

        if (duplicatePeriods.length > 0) {
          const duplicateMessages = duplicatePeriods.map(
            (dup) =>
              `Perioada ${dup.period1} si Perioada ${dup.period2} (${moment(
                dup.startDate
              ).format("DD.MM.YYYY")} - ${moment(dup.endDate).format(
                "DD.MM.YYYY"
              )})`
          );
          handleStatus(
            `Urmatoarele perioade au date identice si trebuie sa fie diferite: ${duplicateMessages.join(
              ", "
            )}`
          );
          return;
        }

        // Check for periods without videos
        const periodsWithoutVideos = schedules
          .map((schedule, index) => ({ schedule, index }))
          .filter(
            ({ schedule }) => !schedule.videos || schedule.videos.length === 0
          )
          .map(({ index }) => index + 1);

        if (periodsWithoutVideos.length > 0) {
          const periodText =
            periodsWithoutVideos.length === 1
              ? `Perioada ${periodsWithoutVideos[0]} nu are`
              : `Perioadele ${periodsWithoutVideos.join(", ")} nu au`;

          handleStatus(
            `${periodText} niciun spot adaugat. Adauga cel putin un spot pentru fiecare perioada.`
          );
          return;
        }

        // If all validations pass, then submit the form
        if (campaignFormikRef.current)
          await campaignFormikRef.current.submitForm();
      } else if (
        activeTab === 1 &&
        newTab === 0 &&
        loopStepRef.current?.saveLoopData
      ) {
        await loopStepRef.current.saveLoopData();
      }
      setActiveTab(newTab);
    } catch (error) {
      handleStatus(error.message || "Something went wrong");
    }
  };

  const handleCampaignChange = useCallback(
    (newCampaignData) =>
      setCampaignData((prev) => ({ ...prev, ...newCampaignData })),
    []
  );

  const handleLoopsChange = useCallback(
    (updatedValues) => setLoopsData((prev) => ({ ...prev, ...updatedValues })),
    []
  );

  const handleStatus = (description) => {
    setToastStatus(true);
    setToastDescription(description);
    setTimeout(() => {
      setToastStatus(false);
      setToastDescription("");
    }, 5000);
  };

  const handleSubmitAll = async (isDraft = false) => {
    if (loading) return;

    setLoading(true);

    let allCampaignIds = [];
    let createdCampaign = null;
    let shouldNavigate = true;

    try {
      let latestLoopData = loopsData;
      if (loopStepRef.current?.saveLoopData) {
        latestLoopData = await loopStepRef.current.saveLoopData();
        setLoopsData(latestLoopData);
      }

      // For drafts, skip validation but still require name and client
      if (!campaignData.name.trim() || !campaignData.client.trim()) {
        handleStatus("Campaign name and client name are required.");
        setLoading(false);
        return;
      }

      const { name, client, observations, schedules } = campaignData;

      const processedSchedules = [];

      // For drafts, schedules are optional
      if (schedules && schedules.length > 0) {
        for (
          let scheduleIndex = 0;
          scheduleIndex < schedules.length;
          scheduleIndex++
        ) {
          const schedule = schedules[scheduleIndex];
          const scheduleVideos = [];

          if (schedule.videos && schedule.videos.length > 0) {
            for (
              let videoIndex = 0;
              videoIndex < schedule.videos.length;
              videoIndex++
            ) {
              const video = schedule.videos[videoIndex];
              const frequency = schedule.videoFrequency ? (schedule.videoFrequency[videoIndex] || 1) : 1;

              // Only handle existing videos (no new video uploads)
              if (video.id || video.ID) {
                scheduleVideos.push({
                  id: video.id || video.ID,
                  frequency: frequency,
                });
              }
            }
          }

          processedSchedules.push({
            startDate: schedule.startDate,
            endDate: schedule.endDate,
            videos: scheduleVideos,
          });
        }
      } else if (!isDraft) {
        // Only require schedules for non-draft campaigns
        handleStatus("At least one schedule is required for active campaigns.");
        setLoading(false);
        return;
      }

      // Create the campaign with schedules containing their individual videos
      const campaignPayload = {
        name,
        client,
        observations,
        schedules: processedSchedules,
        devices: latestLoopData.selectedDevices ? latestLoopData.selectedDevices.map((d) => d.device_id) : [],
      };

      const campaignResponse = isDraft 
        ? await CreateCampaignDraft(JSON.stringify(campaignPayload))
        : await CreateCampaign(JSON.stringify(campaignPayload));
      if (campaignResponse.status !== "OK") {
        throw new Error(`Failed to create campaign: ${campaignResponse.error}`);
      }

      createdCampaign = campaignResponse.data;
      allCampaignIds = campaignResponse.allCampaignIds || [createdCampaign.ID];

      setCreatedCampaignID(createdCampaign.ID);

      // Skip playlist generation for drafts
      if (!isDraft) {
        // Try to generate playlists for all campaigns
        let hasGenerationTriggered = false;
        for (const campaignId of allCampaignIds) {
          try {
            const generateResponse = await GenerateCampaignPlaylist(campaignId);
            if (
              generateResponse.status === "OK" &&
              generateResponse.regenerationTriggered
            ) {
              hasGenerationTriggered = true;
            }
          } catch (genError) {
            console.warn(
              `Failed to generate playlist for campaign ${campaignId}:`,
              genError
            );
          }
        }

        if (hasGenerationTriggered) {
          setAlertModalText(
            "Campania a fost creata cu succes. Regenerarea playlist-ului a fost initiata."
          );
          setOpenModal(true);
          shouldNavigate = false;

          setTimeout(() => {
            setOpenModal(false);
            navigate("/campaigns");
          }, 2500);
          return;
        }
      } else {
        // For drafts, show success message without playlist generation
        setAlertModalText(
          "Campania draft a fost salvata cu succes."
        );
        setOpenModal(true);
        shouldNavigate = false;

        setTimeout(() => {
          setOpenModal(false);
          navigate("/campaigns");
        }, 2500);
        return;
      }

      navigate("/campaigns");
    } catch (error) {
      if (allCampaignIds && allCampaignIds.length > 0) {
        for (const campaignId of allCampaignIds) {
          try {
            await DeleteCampaign(campaignId, false);
          } catch (deleteError) {
            console.error(
              `Failed to cleanup campaign ${campaignId}:`,
              deleteError
            );
          }
        }
      }

      handleStatus(error.message || "Something went wrong");
    } finally {
      setLoading(false);
      if (
        shouldNavigate &&
        createdCampaign &&
        allCampaignIds &&
        allCampaignIds.length > 0
      ) {
        navigate("/campaigns");
      }
    }
  };

  return (
    <div>
      {toastStatus && <PopSnackbar message={toastDescription} />}
      <StyledTabs
        value={activeTab}
        onChange={handleTabChange}
        variant="fullWidth"
      >
        <StyledTab
          icon={
            <div style={{ display: "flex", alignItems: "center" }}>
              <StepIndicator active={activeTab === 0} completed={activeTab > 0}>
                {activeTab > 0 ? <Check fontSize="small" /> : "1"}
              </StepIndicator>
              <CampaignIcon />
            </div>
          }
          label="Configureaza campania"
          iconPosition="start"
        />
        <StyledTab
          icon={
            <div style={{ display: "flex", alignItems: "center" }}>
              <StepIndicator active={activeTab === 1} completed={activeTab > 1}>
                2
              </StepIndicator>
              <LoopIcon />
            </div>
          }
          label="Selectie playere"
          iconPosition="start"
        />
        <ProgressArrow activeTab={activeTab} />
      </StyledTabs>

      <TabPanel hidden={activeTab !== 0}>
        <AddCampaignStep
          data={campaignData}
          onChange={handleCampaignChange}
          handleStatus={handleStatus}
          goNext={() => setActiveTab(1)}
          formikRef={campaignFormikRef}
        />
      </TabPanel>

      <TabPanel hidden={activeTab !== 1}>
        <AddLoopStep
          data={loopsData}
          onChange={handleLoopsChange}
          goBack={() => setActiveTab(0)}
          campaignData={campaignData}
          handleStatus={handleStatus}
          onSubmitAll={handleSubmitAll}
          loadingbtn={loading}
          ref={loopStepRef}
        />
      </TabPanel>
      <AlertModal
        open={openModal}
        text={alertModalText}
        handleClose={() => {
          setOpenModal(false);
          navigate(`/devices`, {
            state: {
              campaignId: createdCampaignID,
            },
          });
        }}
        showYesButton={false}
        showNoButton={false}
        showOkButton={true}
      />
    </div>
  );
}
